import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { SOCCER_LEAGUES, SOCCER_CLUBS, PREDICTION_CATEGORIES } from '../constants/soccerData';
import { EUROPEAN_TOURNAMENTS } from '../constants/europeanTournaments';
import './AdminDashboard.css';
import ClockTimePicker from '../components/ClockTimePicker';
import firebaseService from '../services/firebaseService';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [matches, setMatches] = useState([]);
  const navigate = useNavigate();
  const [newMatch, setNewMatch] = useState({
    league: '',
    homeTeam: '',
    awayTeam: '',
    matchTime: '',
    date: '',
    prediction: '',
    predictionCategory: '',
    coefficient: '',
    status: 'pending',
    homeScore: '',
    awayScore: '',
    predictedScore: '',
    livescoreUrl: '',
    category: 'regular',
    confidence: '3',
    stake: '1',
    riskLevel: 'medium',
    comment: '',
    analysis: '',
    customPrediction: ''
  });
  const [editingMatch, setEditingMatch] = useState(null);
  const [stats, setStats] = useState({
    totalMatches: 0,
    winRate: 0,
    totalPredictions: 0,
    activeMatches: 0
  });
  
    // State for league management
  const [leagues, setLeagues] = useState([...SOCCER_LEAGUES]);
  const [clubs, setClubs] = useState({...SOCCER_CLUBS});
  const [selectedLeague, setSelectedLeague] = useState('');
  const [newLeagueName, setNewLeagueName] = useState('');
  const [newClubName, setNewClubName] = useState('');
  const [editingClubIndex, setEditingClubIndex] = useState(null);
  const [editingClubName, setEditingClubName] = useState('');
  const [confirmDeleteLeague, setConfirmDeleteLeague] = useState(null);
  const [confirmDeleteMatch, setConfirmDeleteMatch] = useState(null);

  // State for prediction category management
  const [predictionCategories, setPredictionCategories] = useState({...PREDICTION_CATEGORIES});
  const [selectedCategory, setSelectedCategory] = useState('');
  const [newCategoryKey, setNewCategoryKey] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newPredictionValue, setNewPredictionValue] = useState('');
  const [editingPredictionIndex, setEditingPredictionIndex] = useState(null);
  const [editingPredictionValue, setEditingPredictionValue] = useState('');
  const [editingCategoryKey, setEditingCategoryKey] = useState(null);
  const [editingCategoryName, setEditingCategoryName] = useState('');

  // State for tournament management
  const [tournaments, setTournaments] = useState({...EUROPEAN_TOURNAMENTS});
  const [selectedTournament, setSelectedTournament] = useState('');
  const [newTournamentTeamName, setNewTournamentTeamName] = useState('');
  const [newTournamentTeamCountry, setNewTournamentTeamCountry] = useState('');
  const [editingTeamIndex, setEditingTeamIndex] = useState(null);
  const [editingTeamName, setEditingTeamName] = useState('');
  const [editingTeamCountry, setEditingTeamCountry] = useState('');

  // State for posts management
  const [posts, setPosts] = useState([]);
  const [postForm, setPostForm] = useState({
    title: '',
    content: '',
    category: '',
    author: 'NTF Admin',
    image: '',
    published: false
  });
  const [editingPost, setEditingPost] = useState(null);

  useEffect(() => {
    // Check authentication - require login to access admin dashboard
    const token = localStorage.getItem('ntf_admin_token');
    if (!token || token !== 'authenticated') {
      console.log('No valid admin token found, redirecting to login...');
      navigate('/shesvla');
      return;
    }

    // Load configuration and matches from Firebase
    const loadData = async () => {
      try {
        // Load global configuration
        const config = await firebaseService.getConfiguration();
        if (config) {
          if (config.leagues) setLeagues(config.leagues);
          if (config.clubs) setClubs(config.clubs);
          if (config.predictionCategories) setPredictionCategories(config.predictionCategories);
          if (config.tournaments) setTournaments(config.tournaments);
        } else {
          // No config exists - initialize Firebase with default values
          console.log('Initializing Firebase with default configuration...');
          const defaultConfig = {
            leagues: SOCCER_LEAGUES,
            clubs: SOCCER_CLUBS,
            predictionCategories: PREDICTION_CATEGORIES,
            tournaments: EUROPEAN_TOURNAMENTS
          };
          await firebaseService.saveConfiguration(defaultConfig);
          console.log('Default configuration saved to Firebase');
        }

        // Migrate localStorage data to Firebase if exists
        await firebaseService.migrateLocalStorageToFirebase();
        
        // Load matches from Firebase
        const firebaseMatches = await firebaseService.getAllMatches();
        setMatches(firebaseMatches);
        updateStats(firebaseMatches);
        
        // Load posts from Firebase
        const firebasePosts = await firebaseService.getAllPosts();
        setPosts(firebasePosts);
        
        // Set up real-time listener for matches
        const unsubscribeMatches = await firebaseService.subscribeToMatches((updatedMatches) => {
          setMatches(updatedMatches);
          updateStats(updatedMatches);
        });

        // Set up real-time listener for posts
        const unsubscribePosts = await firebaseService.subscribeToPosts((updatedPosts) => {
          setPosts(updatedPosts);
        });

        // Set up real-time listener for configuration
        const unsubscribeConfig = await firebaseService.subscribeToConfiguration((updatedConfig) => {
          if (updatedConfig) {
            if (updatedConfig.leagues) setLeagues(updatedConfig.leagues);
            if (updatedConfig.clubs) setClubs(updatedConfig.clubs);
            if (updatedConfig.predictionCategories) setPredictionCategories(updatedConfig.predictionCategories);
          }
        });
        
        // Cleanup function
        return () => {
          unsubscribeMatches();
          unsubscribePosts();
          unsubscribeConfig();
        };
      } catch (error) {
        console.error('Error loading data:', error);
        // Fallback to empty array
        setMatches([]);
        updateStats([]);
      }
    };
    
    loadData();
    
    // Force 24-hour format for time inputs
    const timeInputs = document.querySelectorAll('input[type="time"]');
    timeInputs.forEach(input => {
      input.addEventListener('focus', () => {
        input.setAttribute('data-format', '24');
      });
    });
  }, [navigate]);

  // Auto-update match status to live when time comes
  useEffect(() => {
    const checkMatchTimes = async () => {
      const now = new Date();
      const currentDate = now.toISOString().split('T')[0];
      const currentTime = now.toTimeString().slice(0, 5);
      
      for (const match of matches) {
        if (match.status === 'pending' && 
            match.date === currentDate && 
            match.matchTime === currentTime) {
          try {
            await firebaseService.updateMatch(match.id, { status: 'live' });
          } catch (error) {
            console.error('Error updating match status:', error);
          }
        }
      }
    };
    
    const interval = setInterval(checkMatchTimes, 60000); // Check every minute
    
    return () => clearInterval(interval);
  }, [matches]);

  const updateStats = (matchList) => {
    const total = matchList.length;
    const wins = matchList.filter(m => m.status === 'win').length;
    const active = matchList.filter(m => m.status === 'pending' || m.status === 'live').length;
    
    setStats({
      totalMatches: total,
      winRate: total > 0 ? Math.round((wins / total) * 100) : 0,
      totalPredictions: total,
      activeMatches: active
    });
  };

  const handleAddMatch = async (e) => {
    e.preventDefault();
    try {
      const match = {
        ...newMatch,
        coefficient: parseFloat(newMatch.coefficient).toFixed(2)
      };
      
      await firebaseService.addMatch(match);
      
      setNewMatch({
        league: '',
        homeTeam: '',
        awayTeam: '',
        matchTime: '',
        date: '',
        prediction: '',
        predictionCategory: '',
        coefficient: '',
        status: 'pending',
        homeScore: '',
        awayScore: '',
        predictedScore: '',
        livescoreUrl: '',
        category: 'regular',
        confidence: '3',
        stake: '1',
        riskLevel: 'medium',
        comment: '',
        analysis: '',
        customPrediction: ''
      });
    } catch (error) {
      console.error('Error adding match:', error);
      alert('შეცდომა მატჩის დამატებისას. გთხოვთ სცადოთ ხელახლა.');
    }
  };

  const handleEditMatch = (match) => {
    setEditingMatch(match);
    setActiveTab('edit');
  };

  const handleUpdateMatch = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        ...editingMatch,
        coefficient: parseFloat(editingMatch.coefficient).toFixed(2)
      };
      
      await firebaseService.updateMatch(editingMatch.id, updateData);
      
      setEditingMatch(null);
      setActiveTab('matches');
    } catch (error) {
      console.error('Error updating match:', error);
      alert('შეცდომა მატჩის განახლებისას. გთხოვთ სცადოთ ხელახლა.');
    }
  };

  const handleDeleteMatch = (id) => {
    setConfirmDeleteMatch(id);
  };

  const handleConfirmDeleteMatch = async () => {
    const id = confirmDeleteMatch;
    setConfirmDeleteMatch(null);
    try {
      await firebaseService.deleteMatch(id);
    } catch (error) {
      console.error('Error deleting match:', error);
      alert('შეცდომა მატჩის წაშლისას. გთხოვთ სცადოთ ხელახლა.');
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await firebaseService.updateMatch(id, { status: newStatus });
    } catch (error) {
      console.error('Error updating match status:', error);
      alert('შეცდომა სტატუსის განახლებისას. გთხოვთ სცადოთ ხელახლა.');
    }
  };

  const handleLogout = () => {
    // Clear authentication token and redirect to login
    localStorage.removeItem('ntf_admin_token');
    navigate('/shesvla');
  };

  // League and Club Management Functions
  const handleAddLeague = async () => {
    if (!newLeagueName.trim()) {
      alert('გთხოვთ შეიყვანოთ ლიგის სახელი');
      return;
    }
    if (leagues.includes(newLeagueName)) {
      alert('ეს ლიგა უკვე არსებობს');
      return;
    }
    
    try {
      const updatedLeagues = [...leagues, newLeagueName];
      const updatedClubs = {...clubs, [newLeagueName]: []};
      
      console.log('handleAddLeague: Saving to Firebase...', {updatedLeagues, updatedClubs});
      
      // Save to Firebase FIRST
      const leaguesSuccess = await firebaseService.updateLeagues(updatedLeagues);
      const clubsSuccess = await firebaseService.updateClubs(updatedClubs);
      
      if (leaguesSuccess && clubsSuccess) {
        console.log('handleAddLeague: Firebase save successful');
        // Update local state AFTER Firebase confirmation
        setLeagues(updatedLeagues);
        setClubs(updatedClubs);
        setNewLeagueName('');
        alert('ლიგა დაემატა წარმატებით');
      } else {
        console.error('handleAddLeague: Firebase save failed');
        alert('შეცდომა: ლიგის დამატება ვერ მოხერხდა. გთხოვთ სცადოთ თავიდან');
      }
    } catch (error) {
      console.error('handleAddLeague: Error occurred:', error);
      alert('შეცდომა: ' + error.message);
    }
  };

  const handleRemoveLeague = (leagueName) => {
    setConfirmDeleteLeague(leagueName);
  };

  const handleConfirmDeleteLeague = async () => {
    const leagueName = confirmDeleteLeague;
    setConfirmDeleteLeague(null);
    try {
      const updatedLeagues = leagues.filter(l => l !== leagueName);
      const updatedClubs = {...clubs};
      delete updatedClubs[leagueName];

      console.log('handleRemoveLeague: Saving to Firebase...', {updatedLeagues, updatedClubs});

      const leaguesSuccess = await firebaseService.updateLeagues(updatedLeagues);
      const clubsSuccess = await firebaseService.updateClubs(updatedClubs);

      if (leaguesSuccess && clubsSuccess) {
        setLeagues(updatedLeagues);
        setClubs(updatedClubs);
        if (selectedLeague === leagueName) {
          setSelectedLeague('');
        }
      } else {
        console.error('handleRemoveLeague: Firebase save failed');
      }
    } catch (error) {
      console.error('handleRemoveLeague: Error occurred:', error);
    }
  };

  const handleAddClub = async () => {
    if (!selectedLeague) {
      alert('გთხოვთ აირჩიოთ ლიგა');
      return;
    }
    if (!newClubName.trim()) {
      alert('გთხოვთ შეიყვანოთ კლუბის სახელი');
      return;
    }
    const leagueClubs = clubs[selectedLeague] || [];
    if (leagueClubs.includes(newClubName)) {
      alert('ეს კლუბი უკვე არსებობს ამ ლიგაში');
      return;
    }
    
    try {
      const updatedClubs = {
        ...clubs,
        [selectedLeague]: [...leagueClubs, newClubName]
      };
      
      console.log('handleAddClub: Saving to Firebase...', {selectedLeague, newClubName, updatedClubs});
      
      // Save to Firebase FIRST
      const success = await firebaseService.updateClubs(updatedClubs);
      
      if (success) {
        console.log('handleAddClub: Firebase save successful');
        // Update local state AFTER Firebase confirmation
        setClubs(updatedClubs);
        setNewClubName('');
        alert('კლუბი დაემატა წარმატებით');
      } else {
        console.error('handleAddClub: Firebase save failed');
        alert('შეცდომა: კლუბის დამატება ვერ მოხერხდა. გთხოვთ სცადოთ თავიდან');
      }
    } catch (error) {
      console.error('handleAddClub: Error occurred:', error);
      alert('შეცდომა: ' + error.message);
    }
  };

  const handleRemoveClub = async (clubName) => {
    if (window.confirm(`დარწმუნებული ხართ, რომ გსურთ "${clubName}" კლუბის წაშლა?`)) {
      try {
        const leagueClubs = clubs[selectedLeague] || [];
        const updatedClubs = {
          ...clubs,
          [selectedLeague]: leagueClubs.filter(c => c !== clubName)
        };
        
        console.log('handleRemoveClub: Saving to Firebase...', {selectedLeague, clubName, updatedClubs});
        
        // Save to Firebase FIRST
        const success = await firebaseService.updateClubs(updatedClubs);
        
        if (success) {
          console.log('handleRemoveClub: Firebase save successful');
          // Update local state AFTER Firebase confirmation
          setClubs(updatedClubs);
          alert('კლუბი წაშლილია წარმატებით');
        } else {
          console.error('handleRemoveClub: Firebase save failed');
          alert('შეცდომა: კლუბის წაშლა ვერ მოხერხდა. გთხოვთ სცადოთ თავიდან');
        }
      } catch (error) {
        console.error('handleRemoveClub: Error occurred:', error);
        alert('შეცდომა: ' + error.message);
      }
    }
  };

  const handleStartEditClub = (index, clubName) => {
    setEditingClubIndex(index);
    setEditingClubName(clubName);
  };

  const handleSaveEditClub = async (index) => {
    if (!editingClubName.trim()) {
      alert('გთხოვთ შეიყვანოთ კლუბის სახელი');
      return;
    }
    
    try {
      const leagueClubs = [...(clubs[selectedLeague] || [])];
      leagueClubs[index] = editingClubName;
      const updatedClubs = {
        ...clubs,
        [selectedLeague]: leagueClubs
      };
      
      console.log('handleSaveEditClub: Saving to Firebase...', {selectedLeague, index, newName: editingClubName, updatedClubs});
      
      // Save to Firebase FIRST
      const success = await firebaseService.updateClubs(updatedClubs);
      
      if (success) {
        console.log('handleSaveEditClub: Firebase save successful');
        // Update local state AFTER Firebase confirmation
        setClubs(updatedClubs);
        setEditingClubIndex(null);
        setEditingClubName('');
        alert('კლუბის სახელი შეცვლილია წარმატებით');
      } else {
        console.error('handleSaveEditClub: Firebase save failed');
        alert('შეცდომა: კლუბის სახელის შეცვლა ვერ მოხერხდა. გთხოვთ სცადოთ თავიდან');
      }
    } catch (error) {
      console.error('handleSaveEditClub: Error occurred:', error);
      alert('შეცდომა: ' + error.message);
    }
  };

  const handleCancelEditClub = () => {
    setEditingClubIndex(null);
    setEditingClubName('');
  };

  const handleMoveClubUp = async (index) => {
    if (index === 0) return;
    try {
      const leagueClubs = [...(clubs[selectedLeague] || [])];
      [leagueClubs[index-1], leagueClubs[index]] = [leagueClubs[index], leagueClubs[index-1]];
      const updatedClubs = {
        ...clubs,
        [selectedLeague]: leagueClubs
      };
      
      console.log('handleMoveClubUp: Saving to Firebase...', {selectedLeague, fromIndex: index, toIndex: index-1});
      
      // Save to Firebase FIRST
      const success = await firebaseService.updateClubs(updatedClubs);
      
      if (success) {
        console.log('handleMoveClubUp: Firebase save successful');
        // Update local state AFTER Firebase confirmation
        setClubs(updatedClubs);
      } else {
        console.error('handleMoveClubUp: Firebase save failed');
        alert('შეცდომა: კლუბის გადაწევა ვერ მოხერხდა. გთხოვთ სცადოთ თავიდან');
      }
    } catch (error) {
      console.error('handleMoveClubUp: Error occurred:', error);
    }
  };

  const handleMoveClubDown = async (index) => {
    const leagueClubs = clubs[selectedLeague] || [];
    if (index === leagueClubs.length - 1) return;
    try {
      const newClubs = [...leagueClubs];
      [newClubs[index], newClubs[index+1]] = [newClubs[index+1], newClubs[index]];
      const updatedClubs = {
        ...clubs,
        [selectedLeague]: newClubs
      };
      
      console.log('handleMoveClubDown: Saving to Firebase...', {selectedLeague, fromIndex: index, toIndex: index+1});
      
      // Save to Firebase FIRST
      const success = await firebaseService.updateClubs(updatedClubs);
      
      if (success) {
        console.log('handleMoveClubDown: Firebase save successful');
        // Update local state AFTER Firebase confirmation
        setClubs(updatedClubs);
      } else {
        console.error('handleMoveClubDown: Firebase save failed');
        alert('შეცდომა: კლუბის გადაწევა ვერ მოხერხდა. გთხოვთ სცადოთ თავიდან');
      }
    } catch (error) {
      console.error('handleMoveClubDown: Error occurred:', error);
    }
  };

  const handleSaveToFile = () => {
    const data = {
      leagues,
      clubs,
      predictionCategories,
      tournaments,
      lastUpdated: new Date().toISOString()
    };
    const dataStr = JSON.stringify(data, null, 2);
    const dataBlob = new Blob([dataStr], {type: 'application/json'});
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ntf-config-backup-${Date.now()}.json`;
    link.click();
    URL.revokeObjectURL(url);
    alert('მონაცემები შენახულია ფაილში');
  };

  // Tournament Team Management Functions
  const handleAddTournamentTeam = async () => {
    if (!selectedTournament) {
      alert('გთხოვთ აირჩიოთ ტურნირი');
      return;
    }
    if (!newTournamentTeamName.trim()) {
      alert('გთხოვთ შეიყვანოთ გუნდის სახელი');
      return;
    }
    if (!newTournamentTeamCountry.trim()) {
      alert('გთხოვთ შეიყვანოთ ქვეყანა');
      return;
    }
    
    const tournament = tournaments[selectedTournament];
    if (!tournament) {
      alert('ტურნირი ვერ მოიძებნა');
      return;
    }
    
    const teamExists = tournament.teams.some(team => 
      team.name.toLowerCase() === newTournamentTeamName.trim().toLowerCase()
    );
    if (teamExists) {
      alert('ეს გუნდი უკვე არსებობს ამ ტურნირში');
      return;
    }
    
    try {
      const updatedTournaments = {
        ...tournaments,
        [selectedTournament]: {
          ...tournament,
          teams: [...tournament.teams, {
            name: newTournamentTeamName.trim(),
            country: newTournamentTeamCountry.trim(),
            confederation: 'UEFA'
          }]
        }
      };
      
      console.log('handleAddTournamentTeam: Saving to Firebase...', {
        selectedTournament, 
        teamName: newTournamentTeamName, 
        country: newTournamentTeamCountry
      });
      
      // Save to Firebase FIRST
      const success = await firebaseService.updateTournaments(updatedTournaments);
      
      if (success) {
        console.log('handleAddTournamentTeam: Firebase save successful');
        // Update local state AFTER Firebase confirmation
        setTournaments(updatedTournaments);
        setNewTournamentTeamName('');
        setNewTournamentTeamCountry('');
        alert('გუნდი დაემატა წარმატებით');
      } else {
        console.error('handleAddTournamentTeam: Firebase save failed');
        alert('შეცდომა: გუნდის დამატება ვერ მოხერხდა. გთხოვთ სცადოთ თავიდან');
      }
    } catch (error) {
      console.error('handleAddTournamentTeam: Error occurred:', error);
      alert('შეცდომა: ' + error.message);
    }
  };

  const handleRemoveTournamentTeam = async (teamIndex) => {
    const tournament = tournaments[selectedTournament];
    const teamName = tournament.teams[teamIndex].name;
    
    if (window.confirm(`დარწმუნებული ხართ, რომ გსურთ "${teamName}" გუნდის წაშლა?`)) {
      try {
        const updatedTournaments = {
          ...tournaments,
          [selectedTournament]: {
            ...tournament,
            teams: tournament.teams.filter((_, index) => index !== teamIndex)
          }
        };
        
        console.log('handleRemoveTournamentTeam: Saving to Firebase...', {
          selectedTournament, 
          teamIndex, 
          teamName
        });
        
        // Save to Firebase FIRST
        const success = await firebaseService.updateTournaments(updatedTournaments);
        
        if (success) {
          console.log('handleRemoveTournamentTeam: Firebase save successful');
          // Update local state AFTER Firebase confirmation
          setTournaments(updatedTournaments);
          alert('გუნდი წაშლილია წარმატებით');
        } else {
          console.error('handleRemoveTournamentTeam: Firebase save failed');
          alert('შეცდომა: გუნდის წაშლა ვერ მოხერხდა. გთხოვთ სცადოთ თავიდან');
        }
      } catch (error) {
        console.error('handleRemoveTournamentTeam: Error occurred:', error);
        alert('შეცდომა: ' + error.message);
      }
    }
  };

  const handleStartEditTeam = (index, teamName, teamCountry) => {
    setEditingTeamIndex(index);
    setEditingTeamName(teamName);
    setEditingTeamCountry(teamCountry);
  };

  const handleSaveEditTeam = async (index) => {
    if (!editingTeamName.trim()) {
      alert('გთხოვთ შეიყვანოთ გუნდის სახელი');
      return;
    }
    if (!editingTeamCountry.trim()) {
      alert('გთხოვთ შეიყვანოთ ქვეყანა');
      return;
    }
    
    try {
      const tournament = tournaments[selectedTournament];
      const updatedTeams = [...tournament.teams];
      updatedTeams[index] = {
        ...updatedTeams[index],
        name: editingTeamName.trim(),
        country: editingTeamCountry.trim()
      };
      
      const updatedTournaments = {
        ...tournaments,
        [selectedTournament]: {
          ...tournament,
          teams: updatedTeams
        }
      };
      
      console.log('handleSaveEditTeam: Saving to Firebase...', {
        selectedTournament, 
        index, 
        newName: editingTeamName,
        newCountry: editingTeamCountry
      });
      
      // Save to Firebase FIRST
      const success = await firebaseService.updateTournaments(updatedTournaments);
      
      if (success) {
        console.log('handleSaveEditTeam: Firebase save successful');
        // Update local state AFTER Firebase confirmation
        setTournaments(updatedTournaments);
        setEditingTeamIndex(null);
        setEditingTeamName('');
        setEditingTeamCountry('');
        alert('გუნდის მონაცემები შეცვლილია წარმატებით');
      } else {
        console.error('handleSaveEditTeam: Firebase save failed');
        alert('შეცდომა: გუნდის მონაცემების შეცვლა ვერ მოხერხდა. გთხოვთ სცადოთ თავიდან');
      }
    } catch (error) {
      console.error('handleSaveEditTeam: Error occurred:', error);
      alert('შეცდომა: ' + error.message);
    }
  };

  const handleCancelEditTeam = () => {
    setEditingTeamIndex(null);
    setEditingTeamName('');
    setEditingTeamCountry('');
  };

  // Posts Management Functions
  const handleAddPost = async (e, shouldPublish = false) => {
    e.preventDefault();
    
    if (!postForm.title.trim()) {
      alert('გთხოვთ შეიყვანოთ სათაური');
      return;
    }
    if (!postForm.content.trim()) {
      alert('გთხოვთ შეიყვანოთ კონტენტი');
      return;
    }

    try {
      const postData = {
        ...postForm,
        published: shouldPublish
      };
      
      const newPost = await firebaseService.addPost(postData);
      if (newPost) {
        alert(shouldPublish ? 'კომენტარი გამოქვეყნდა წარმატებით!' : 'კომენტარი შენახულია წარმატებით!');
        setPostForm({
          title: '',
          content: '',
          category: '',
          author: 'NTF Admin',
          image: '',
          published: false
        });
      } else {
        alert('შეცდომა: კომენტარის დამატება ვერ მოხერხდა');
      }
    } catch (error) {
      console.error('Error adding post:', error);
      alert('შეცდომა: ' + error.message);
    }
  };

  const handleUpdatePost = async (e, shouldPublish = false) => {
    e.preventDefault();
    
    if (!editingPost) return;
    
    try {
      const updateData = {
        ...postForm,
        published: shouldPublish !== undefined ? shouldPublish : postForm.published
      };
      
      const success = await firebaseService.updatePost(editingPost.id, updateData);
      if (success) {
        alert(shouldPublish ? 'კომენტარი გამოქვეყნდა წარმატებით!' : 'კომენტარი განახლდა წარმატებით!');
        setEditingPost(null);
        setPostForm({
          title: '',
          content: '',
          category: '',
          author: 'NTF Admin',
          image: '',
          published: false
        });
      } else {
        alert('შეცდომა: კომენტარის განახლება ვერ მოხერხდა');
      }
    } catch (error) {
      console.error('Error updating post:', error);
      alert('შეცდომა: ' + error.message);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('გთხოვთ აირჩიოთ სურათის ფაილი');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('სურათის ზომა არ უნდა აღემატებოდეს 5MB-ს');
      return;
    }

    try {
      // Convert image to base64
      const reader = new FileReader();
      reader.onload = (event) => {
        setPostForm({...postForm, image: event.target.result});
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('შეცდომა სურათის ატვირთვისას');
    }
  };

  const handleDeletePost = async (postId) => {
    if (window.confirm('დარწმუნებული ხართ, რომ გსურთ სიახლის წაშლა?')) {
      try {
        const success = await firebaseService.deletePost(postId);
        if (success) {
          alert('სიახლე წაშლილია წარმატებით');
        } else {
          alert('შეცდომა: სიახლის წაშლა ვერ მოხერხდა');
        }
      } catch (error) {
        console.error('Error deleting post:', error);
        alert('შეცდომა: ' + error.message);
      }
    }
  };

  const handleEditPost = (post) => {
    setEditingPost(post);
    setPostForm({
      title: post.title,
      content: post.content,
      category: post.category || '',
      author: post.author || 'NTF Admin',
      image: post.image || '',
      published: post.published
    });
  };

  const handleCancelEditPost = () => {
    setEditingPost(null);
    setPostForm({
      title: '',
      content: '',
      category: '',
      author: 'NTF Admin',
      image: '',
      published: false
    });
  };

  const handleTogglePublish = async (post) => {
    try {
      const success = await firebaseService.updatePost(post.id, {
        published: !post.published
      });
      if (success) {
        alert(post.published ? 'სიახლე მოხსნილია პუბლიკაციიდან' : 'სიახლე გამოქვეყნდა');
      }
    } catch (error) {
      console.error('Error toggling publish:', error);
      alert('შეცდომა: ' + error.message);
    }
  };

  // Prediction Category Management Functions
  const handleAddCategory = async () => {
    if (!newCategoryKey.trim() || !newCategoryName.trim()) {
      alert('გთხოვთ შეიყვანოთ კატეგორიის გასაღები და სახელი');
      return;
    }
    if (predictionCategories[newCategoryKey]) {
      alert('ეს კატეგორია უკვე არსებობს');
      return;
    }
    const updatedCategories = {
      ...predictionCategories,
      [newCategoryKey]: {
        name: newCategoryName,
        predictions: []
      }
    };
    
    setPredictionCategories(updatedCategories);
    setNewCategoryKey('');
    setNewCategoryName('');
    
    // Save to Firebase
    await firebaseService.updatePredictionCategories(updatedCategories);
    
    alert('კატეგორია დაემატა წარმატებით');
  };

  const handleRemoveCategory = async (categoryKey) => {
    const categoryName = predictionCategories[categoryKey]?.name || categoryKey;
    if (window.confirm(`დარწმუნებული ხართ, რომ გსურთ "${categoryName}" კატეგორიის წაშლა?`)) {
      const updatedCategories = {...predictionCategories};
      delete updatedCategories[categoryKey];
      
      setPredictionCategories(updatedCategories);
      if (selectedCategory === categoryKey) {
        setSelectedCategory('');
      }
      
      // Save to Firebase
      await firebaseService.updatePredictionCategories(updatedCategories);
      
      alert('კატეგორია წაშლილია წარმატებით');
    }
  };

  const handleStartEditCategory = (categoryKey) => {
    setEditingCategoryKey(categoryKey);
    setEditingCategoryName(predictionCategories[categoryKey]?.name || '');
  };

  const handleSaveEditCategory = async () => {
    if (!editingCategoryName.trim()) {
      alert('გთხოვთ შეიყვანოთ კატეგორიის სახელი');
      return;
    }
    const updatedCategories = {
      ...predictionCategories,
      [editingCategoryKey]: {
        ...predictionCategories[editingCategoryKey],
        name: editingCategoryName
      }
    };
    
    setPredictionCategories(updatedCategories);
    setEditingCategoryKey(null);
    setEditingCategoryName('');
    
    // Save to Firebase
    await firebaseService.updatePredictionCategories(updatedCategories);
    
    alert('კატეგორიის სახელი შეცვლილია წარმატებით');
  };

  const handleCancelEditCategory = () => {
    setEditingCategoryKey(null);
    setEditingCategoryName('');
  };

  const handleAddPrediction = async () => {
    if (!selectedCategory) {
      alert('გთხოვთ აირჩიოთ კატეგორია');
      return;
    }
    if (!newPredictionValue.trim()) {
      alert('გთხოვთ შეიყვანოთ პროგნოზის მნიშვნელობა');
      return;
    }
    const categoryPredictions = predictionCategories[selectedCategory]?.predictions || [];
    
    // Check if prediction already exists (compare by value if object, or by string)
    const exists = categoryPredictions.some(p => 
      (typeof p === 'string' ? p : p.value) === newPredictionValue
    );
    
    if (exists) {
      alert('ეს პროგნოზი უკვე არსებობს ამ კატეგორიაში');
      return;
    }
    
    // Create prediction object
    const newPrediction = {
      value: newPredictionValue,
      label: newPredictionValue,
      type: selectedCategory.toLowerCase().replace(/_/g, '-')
    };
    
    const updatedCategories = {
      ...predictionCategories,
      [selectedCategory]: {
        ...predictionCategories[selectedCategory],
        predictions: [...categoryPredictions, newPrediction]
      }
    };
    
    setPredictionCategories(updatedCategories);
    setNewPredictionValue('');
    
    // Save to Firebase
    await firebaseService.updatePredictionCategories(updatedCategories);
    
    alert('პროგნოზი დაემატა წარმატებით');
  };

  const handleRemovePrediction = async (predictionValue) => {
    if (window.confirm(`დარწმუნებული ხართ, რომ გსურთ "${predictionValue}" პროგნოზის წაშლა?`)) {
      const categoryPredictions = predictionCategories[selectedCategory]?.predictions || [];
      const updatedCategories = {
        ...predictionCategories,
        [selectedCategory]: {
          ...predictionCategories[selectedCategory],
          predictions: categoryPredictions.filter(p => 
            (typeof p === 'string' ? p : p.value) !== predictionValue
          )
        }
      };
      
      setPredictionCategories(updatedCategories);
      
      // Save to Firebase
      await firebaseService.updatePredictionCategories(updatedCategories);
      
      alert('პროგნოზი წაშლილია წარმატებით');
    }
  };

  const handleStartEditPrediction = (index, predictionValue) => {
    setEditingPredictionIndex(index);
    setEditingPredictionValue(predictionValue);
  };

  const handleSaveEditPrediction = async (index) => {
    if (!editingPredictionValue.trim()) {
      alert('გთხოვთ შეიყვანოთ პროგნოზის მნიშვნელობა');
      return;
    }
    const categoryPredictions = [...(predictionCategories[selectedCategory]?.predictions || [])];
    
    // Update the prediction object
    const currentPrediction = categoryPredictions[index];
    categoryPredictions[index] = typeof currentPrediction === 'string' 
      ? {
          value: editingPredictionValue,
          label: editingPredictionValue,
          type: selectedCategory.toLowerCase().replace(/_/g, '-')
        }
      : {
          ...currentPrediction,
          value: editingPredictionValue,
          label: editingPredictionValue
        };
    
    const updatedCategories = {
      ...predictionCategories,
      [selectedCategory]: {
        ...predictionCategories[selectedCategory],
        predictions: categoryPredictions
      }
    };
    
    setPredictionCategories(updatedCategories);
    setEditingPredictionIndex(null);
    setEditingPredictionValue('');
    
    // Save to Firebase
    await firebaseService.updatePredictionCategories(updatedCategories);
    
    alert('პროგნოზი შეცვლილია წარმატებით');
  };

  const handleCancelEditPrediction = () => {
    setEditingPredictionIndex(null);
    setEditingPredictionValue('');
  };

  const handleMovePredictionUp = async (index) => {
    if (index === 0) return;
    const categoryPredictions = [...(predictionCategories[selectedCategory]?.predictions || [])];
    [categoryPredictions[index-1], categoryPredictions[index]] = [categoryPredictions[index], categoryPredictions[index-1]];
    const updatedCategories = {
      ...predictionCategories,
      [selectedCategory]: {
        ...predictionCategories[selectedCategory],
        predictions: categoryPredictions
      }
    };
    
    setPredictionCategories(updatedCategories);
    
    // Save to Firebase
    await firebaseService.updatePredictionCategories(updatedCategories);
  };

  const handleMovePredictionDown = async (index) => {
    const categoryPredictions = predictionCategories[selectedCategory]?.predictions || [];
    if (index === categoryPredictions.length - 1) return;
    const newPredictions = [...categoryPredictions];
    [newPredictions[index], newPredictions[index+1]] = [newPredictions[index+1], newPredictions[index]];
    const updatedCategories = {
      ...predictionCategories,
      [selectedCategory]: {
        ...predictionCategories[selectedCategory],
        predictions: newPredictions
      }
    };
    
    setPredictionCategories(updatedCategories);
    
    // Save to Firebase
    await firebaseService.updatePredictionCategories(updatedCategories);
  };

  const renderLeagueManagement = () => (
    <div className="league-management">
      <h2 className="management-title">🏆 ლიგებისა და კლუბების მართვა</h2>
      
      <div className="management-grid">
        {/* League Management Section */}
        <div className="management-section">
          <h3 className="section-header">ლიგები ({leagues.length})</h3>
          
          <div className="add-item-form">
            <input
              type="text"
              placeholder="ახალი ლიგის სახელი..."
              value={newLeagueName}
              onChange={(e) => setNewLeagueName(e.target.value)}
              className="management-input"
            />
            <button onClick={handleAddLeague} className="add-btn">
              ➕ დამატება
            </button>
          </div>

          <div className="leagues-list">
            {leagues.map((league) => (
              <div
                key={league}
                className={`league-item ${selectedLeague === league ? 'selected' : ''} ${confirmDeleteLeague === league ? 'confirm-delete-active' : ''}`}
                onClick={() => { if (confirmDeleteLeague !== league) setSelectedLeague(league); }}
              >
                {confirmDeleteLeague === league ? (
                  <div className="league-delete-confirm" onClick={(e) => e.stopPropagation()}>
                    <span className="confirm-text">წაშლა: <strong>{league}</strong>?</span>
                    <div className="confirm-actions">
                      <button
                        className="confirm-yes-btn"
                        onClick={handleConfirmDeleteLeague}
                      >
                        ✓ დიახ
                      </button>
                      <button
                        className="confirm-no-btn"
                        onClick={() => setConfirmDeleteLeague(null)}
                      >
                        ✕ არა
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <span className="league-name">{league}</span>
                    <span className="club-count">({clubs[league]?.length || 0} კლუბი)</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveLeague(league);
                      }}
                      className="delete-btn-small"
                    >
                      🗑️
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Club Management Section */}
        <div className="management-section">
          <h3 className="section-header">
            {selectedLeague ? `${selectedLeague} - კლუბები` : 'აირჩიეთ ლიგა'}
          </h3>
          
          {selectedLeague && (
            <>
              <div className="add-item-form">
                <input
                  type="text"
                  placeholder="ახალი კლუბის სახელი..."
                  value={newClubName}
                  onChange={(e) => setNewClubName(e.target.value)}
                  className="management-input"
                />
                <button onClick={handleAddClub} className="add-btn">
                  ➕ დამატება
                </button>
              </div>

              <div className="clubs-list">
                {(clubs[selectedLeague] || []).map((club, index) => (
                  <div key={index} className="club-item">
                    {editingClubIndex === index ? (
                      <div className="edit-club-form">
                        <input
                          type="text"
                          value={editingClubName}
                          onChange={(e) => setEditingClubName(e.target.value)}
                          className="edit-input"
                          autoFocus
                        />
                        <button onClick={() => handleSaveEditClub(index)} className="save-btn-small">
                          ✓
                        </button>
                        <button onClick={handleCancelEditClub} className="cancel-btn-small">
                          ✕
                        </button>
                      </div>
                    ) : (
                      <>
                        <span className="club-name">{club}</span>
                        <div className="club-actions">
                          <button
                            onClick={() => handleMoveClubUp(index)}
                            disabled={index === 0}
                            className="move-btn"
                            title="ზემოთ"
                          >
                            ↑
                          </button>
                          <button
                            onClick={() => handleMoveClubDown(index)}
                            disabled={index === (clubs[selectedLeague]?.length || 0) - 1}
                            className="move-btn"
                            title="ქვემოთ"
                          >
                            ↓
                          </button>
                          <button
                            onClick={() => handleStartEditClub(index, club)}
                            className="edit-btn-small"
                            title="რედაქტირება"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => handleRemoveClub(club)}
                            className="delete-btn-small"
                            title="წაშლა"
                          >
                            🗑️
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="management-actions">
        <button onClick={handleSaveToFile} className="export-btn">
          💾 მონაცემების შენახვა (Backup)
        </button>
        <div className="info-text">
          💡 ცვლილებები ავტომატურად ინახება Firebase-ში და მუდმივია.
        </div>
      </div>
    </div>
  );

  const renderPredictionManagement = () => (
    <div className="league-management">
      <h2 className="management-title">🎯 პროგნოზების კატეგორიების მართვა</h2>
      
      <div className="management-grid">
        {/* Category Management Section */}
        <div className="management-section">
          <h3 className="section-header">კატეგორიები ({Object.keys(predictionCategories).length})</h3>
          
          <div className="add-item-form">
            <input
              type="text"
              placeholder="კატეგორიის გასაღები (მაგ: goals)"
              value={newCategoryKey}
              onChange={(e) => setNewCategoryKey(e.target.value)}
              className="management-input"
              style={{marginBottom: '5px'}}
            />
            <input
              type="text"
              placeholder="კატეგორიის სახელი (მაგ: 🎯 გოლები)"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              className="management-input"
            />
            <button onClick={handleAddCategory} className="add-btn">
              ➕ დამატება
            </button>
          </div>

          <div className="leagues-list">
            {Object.entries(predictionCategories).map(([key, category]) => (
              <div
                key={key}
                className={`league-item ${selectedCategory === key ? 'selected' : ''}`}
                onClick={() => setSelectedCategory(key)}
              >
                {editingCategoryKey === key ? (
                  <div className="edit-club-form" style={{width: '100%'}}>
                    <input
                      type="text"
                      value={editingCategoryName}
                      onChange={(e) => setEditingCategoryName(e.target.value)}
                      className="edit-input"
                      autoFocus
                    />
                    <button onClick={handleSaveEditCategory} className="save-btn-small">
                      ✓
                    </button>
                    <button onClick={handleCancelEditCategory} className="cancel-btn-small">
                      ✕
                    </button>
                  </div>
                ) : (
                  <>
                    <div>
                      <span className="league-name">{category.name}</span>
                      <span className="club-count" style={{display: 'block', fontSize: '0.8em', opacity: 0.7}}>
                        ({key}) - {category.predictions?.length || 0} პროგნოზი
                      </span>
                    </div>
                    <div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStartEditCategory(key);
                        }}
                        className="edit-btn-small"
                        title="რედაქტირება"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveCategory(key);
                        }}
                        className="delete-btn-small"
                      >
                        🗑️
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Prediction Management Section */}
        <div className="management-section">
          <h3 className="section-header">
            {selectedCategory ? `${predictionCategories[selectedCategory]?.name} - პროგნოზები` : 'აირჩიეთ კატეგორია'}
          </h3>
          
          {selectedCategory && (
            <>
              <div className="add-item-form">
                <input
                  type="text"
                  placeholder="პროგნოზის მნიშვნელობა (მაგ: 8.5 >, 15 <, 2-3 გოლი)"
                  value={newPredictionValue}
                  onChange={(e) => setNewPredictionValue(e.target.value)}
                  className="management-input"
                />
                <button onClick={handleAddPrediction} className="add-btn">
                  ➕ დამატება
                </button>
              </div>

              <div className="clubs-list">
                {(predictionCategories[selectedCategory]?.predictions || []).map((prediction, index) => {
                  const predValue = typeof prediction === 'string' ? prediction : prediction.value;
                  const predLabel = typeof prediction === 'string' ? prediction : prediction.label;
                  
                  return (
                    <div key={index} className="club-item">
                      {editingPredictionIndex === index ? (
                        <div className="edit-club-form">
                          <input
                            type="text"
                            value={editingPredictionValue}
                            onChange={(e) => setEditingPredictionValue(e.target.value)}
                            className="edit-input"
                            autoFocus
                          />
                          <button onClick={() => handleSaveEditPrediction(index)} className="save-btn-small">
                            ✓
                          </button>
                          <button onClick={handleCancelEditPrediction} className="cancel-btn-small">
                            ✕
                          </button>
                        </div>
                      ) : (
                        <>
                          <span className="club-name">{predLabel}</span>
                          <div className="club-actions">
                            <button
                              onClick={() => handleMovePredictionUp(index)}
                              disabled={index === 0}
                              className="move-btn"
                              title="ზემოთ"
                            >
                              ↑
                            </button>
                            <button
                              onClick={() => handleMovePredictionDown(index)}
                              disabled={index === (predictionCategories[selectedCategory]?.predictions?.length || 0) - 1}
                              className="move-btn"
                              title="ქვემოთ"
                            >
                              ↓
                            </button>
                            <button
                              onClick={() => handleStartEditPrediction(index, predValue)}
                              className="edit-btn-small"
                              title="რედაქტირება"
                            >
                              ✏️
                            </button>
                            <button
                              onClick={() => handleRemovePrediction(predValue)}
                              className="delete-btn-small"
                              title="წაშლა"
                            >
                              🗑️
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="management-actions">
        <button onClick={handleSaveToFile} className="export-btn">
          💾 სრული კონფიგურაციის შენახვა (Backup)
        </button>
        <div className="info-text">
          💡 ცვლილებები ავტომატურად ინახება Firebase-ში და მუდმივია.
          <br/>
          თქვენ შეგიძლიათ დაამატოთ ნებისმიერი მნიშვნელობები, მაგალითად: "8.5 {'>'}",  "15 {'<'}", "2-3 გოლი", და ა.შ.
        </div>
      </div>
    </div>
  );

  const renderTournamentManagement = () => (
    <div className="league-management">
      <h2 className="management-title">🏅 ევროპული ტურნირების მართვა</h2>
      
      <div className="management-grid">
        {/* Tournament Selection Section */}
        <div className="management-section">
          <h3 className="section-header">ტურნირები ({Object.keys(tournaments).length})</h3>
          
          <div className="leagues-list">
            {Object.entries(tournaments).map(([key, tournament]) => (
              <div
                key={key}
                className={`league-item ${selectedTournament === key ? 'selected' : ''}`}
                onClick={() => setSelectedTournament(key)}
              >
                <div>
                  <span className="league-name">{tournament.name}</span>
                  <span className="club-count" style={{display: 'block', fontSize: '0.8em', opacity: 0.7}}>
                    {tournament.season} - {tournament.teams?.length || 0} გუნდი
                  </span>
                  <span className="club-count" style={{display: 'block', fontSize: '0.75em', opacity: 0.6, marginTop: '4px'}}>
                    ფინალი: {tournament.finalDate}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Tournament Teams Management Section */}
        <div className="management-section">
          <h3 className="section-header">
            {selectedTournament ? `${tournaments[selectedTournament]?.name} - გუნდების მართვა` : 'აირჩიეთ ტურნირი'}
          </h3>
          
          {selectedTournament && (
            <>
              {/* Tournament Info */}
              <div className="tournament-info" style={{marginBottom: '20px', padding: '12px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px'}}>
                <div style={{fontSize: '0.85em', opacity: 0.8}}>
                  <div>📅 სეზონი: {tournaments[selectedTournament]?.season}</div>
                  <div>🏆 ფორმატი: {tournaments[selectedTournament]?.format}</div>
                  <div>📍 ფინალი: {tournaments[selectedTournament]?.finalDate} - {tournaments[selectedTournament]?.finalLocation}</div>
                </div>
              </div>

              {/* Add Team Form */}
              <div className="add-item-form">
                <input
                  type="text"
                  placeholder="გუნდის სახელი..."
                  value={newTournamentTeamName}
                  onChange={(e) => setNewTournamentTeamName(e.target.value)}
                  className="management-input"
                />
                <input
                  type="text"
                  placeholder="ქვეყანა..."
                  value={newTournamentTeamCountry}
                  onChange={(e) => setNewTournamentTeamCountry(e.target.value)}
                  className="management-input"
                  style={{marginTop: '8px'}}
                />
                <button 
                  onClick={handleAddTournamentTeam}
                  className="add-btn"
                  style={{marginTop: '8px'}}
                >
                  ➕ გუნდის დამატება
                </button>
              </div>

              {/* Teams List */}
              <div className="clubs-list">
                <h4 style={{marginBottom: '12px', opacity: 0.9}}>
                  გუნდები ({tournaments[selectedTournament]?.teams?.length || 0})
                </h4>
                {(tournaments[selectedTournament]?.teams || []).map((team, index) => (
                  <div key={index} className="club-item">
                    {editingTeamIndex === index ? (
                      <div style={{flex: 1}}>
                        <input
                          type="text"
                          value={editingTeamName}
                          onChange={(e) => setEditingTeamName(e.target.value)}
                          className="edit-input"
                          placeholder="გუნდის სახელი"
                        />
                        <input
                          type="text"
                          value={editingTeamCountry}
                          onChange={(e) => setEditingTeamCountry(e.target.value)}
                          className="edit-input"
                          placeholder="ქვეყანა"
                          style={{marginTop: '4px'}}
                        />
                      </div>
                    ) : (
                      <span className="club-name">
                        {team.name}
                        <span style={{opacity: 0.6, marginLeft: '8px', fontSize: '0.85em'}}>({team.country})</span>
                      </span>
                    )}
                    <div className="club-actions">
                      {editingTeamIndex === index ? (
                        <>
                          <button
                            onClick={() => handleSaveEditTeam(index)}
                            className="edit-btn-small"
                            title="შენახვა"
                          >
                            ✅
                          </button>
                          <button
                            onClick={handleCancelEditTeam}
                            className="delete-btn-small"
                            title="გაუქმება"
                          >
                            ❌
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => handleStartEditTeam(index, team.name, team.country)}
                            className="edit-btn-small"
                            title="რედაქტირება"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => handleRemoveTournamentTeam(index)}
                            className="delete-btn-small"
                            title="წაშლა"
                          >
                            🗑️
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="management-actions">
        <button onClick={handleSaveToFile} className="export-btn">
          💾 სრული კონფიგურაციის შენახვა (Backup)
        </button>
        <div className="info-text">
          💡 ცვლილებები ავტომატურად ინახება Firebase-ში და მუდმივია.
          <br/>
          თქვენ შეგიძლიათ დაამატოთ, შეცვალოთ ან წაშალოთ ტურნირის გუნდები.
        </div>
      </div>
    </div>
  );

  const renderPostsManagement = () => (
    <div className="posts-management">
      <h2 className="management-title">📰 კომენტარების მართვა</h2>
      
      {/* Post Form */}
      <div className="post-editor">
        <h3>{editingPost ? 'კომენტარის რედაქტირება' : 'ახალი კომენტარის დამატება'}</h3>
        <form onSubmit={(e) => e.preventDefault()} className="post-form">
          <div className="form-row">
            <div className="form-group" style={{flex: 2}}>
              <label>სათაური *</label>
              <input
                type="text"
                value={postForm.title}
                onChange={(e) => setPostForm({...postForm, title: e.target.value})}
                placeholder="შეიყვანეთ კომენტარის სათაური..."
                required
              />
            </div>
            <div className="form-group">
              <label>კატეგორია *</label>
              <select
                value={postForm.category}
                onChange={(e) => setPostForm({...postForm, category: e.target.value})}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  fontSize: '1rem',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '8px',
                  background: 'rgba(255,255,255,0.05)',
                  color: '#fff'
                }}
                required
              >
                <option value="">აირჩიეთ კატეგორია</option>
                <option value="ანალიზი">📊 ანალიზი</option>
                <option value="სიახლე">📰 სიახლე</option>
                <option value="კომენტარი">💬 კომენტარი</option>
                <option value="პროგნოზი">🎯 პროგნოზი</option>
                <option value="ინტერვიუ">🎤 ინტერვიუ</option>
                <option value="სტატისტიკა">📈 სტატისტიკა</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group" style={{flex: 1}}>
              <label>სურათის ატვირთვა (არჩევითი)</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  fontSize: '0.95rem',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '8px',
                  background: 'rgba(255,255,255,0.05)',
                  color: '#fff',
                  cursor: 'pointer'
                }}
              />
              <small style={{color: 'rgba(255,255,255,0.6)', marginTop: '0.5rem', display: 'block'}}>
                📎 მაქსიმუმ 5MB, JPG, PNG, GIF
              </small>
            </div>
            <div className="form-group" style={{flex: 1}}>
              <label>ან სურათის URL</label>
              <input
                type="url"
                value={postForm.image && !postForm.image.startsWith('data:') ? postForm.image : ''}
                onChange={(e) => setPostForm({...postForm, image: e.target.value})}
                placeholder="https://example.com/image.jpg"
              />
            </div>
          </div>

          {postForm.image && (
            <div className="form-group">
              <label>სურათის გადახედვა</label>
              <div style={{marginTop: '0.5rem'}}>
                <img 
                  src={postForm.image} 
                  alt="Preview"
                  style={{
                    maxWidth: '300px',
                    maxHeight: '200px',
                    borderRadius: '8px',
                    border: '1px solid rgba(255,255,255,0.2)'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    alert('სურათის ჩატვირთვა ვერ მოხერხდა');
                  }}
                />
                <button
                  type="button"
                  onClick={() => setPostForm({...postForm, image: ''})}
                  style={{
                    marginLeft: '1rem',
                    padding: '0.5rem 1rem',
                    background: 'rgba(255,0,0,0.2)',
                    border: '1px solid rgba(255,0,0,0.5)',
                    borderRadius: '6px',
                    color: '#fff',
                    cursor: 'pointer'
                  }}
                >
                  🗑️ წაშლა
                </button>
              </div>
            </div>
          )}

          <div className="form-group">
            <label>კონტენტი * (HTML მხარდაჭერით)</label>
            <textarea
              value={postForm.content}
              onChange={(e) => setPostForm({...postForm, content: e.target.value})}
              placeholder="შეიყვანეთ კომენტარის ტექსტი (შეგიძლიათ გამოიყენოთ HTML თეგები: <p>, <strong>, <em>, <ul>, <li>, <br>, <a>)..."
              rows="15"
              style={{
                width: '100%',
                padding: '1rem',
                fontSize: '1rem',
                lineHeight: '1.6',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '8px',
                background: 'rgba(255,255,255,0.05)',
                color: '#fff',
                fontFamily: 'inherit',
                resize: 'vertical'
              }}
              required
            />
            <small style={{color: 'rgba(255,255,255,0.6)', marginTop: '0.5rem', display: 'block'}}>
              💡 HTML მაგალითები: &lt;p&gt;პარაგრაფი&lt;/p&gt;, &lt;strong&gt;მსხვილი&lt;/strong&gt;, &lt;em&gt;კურსივი&lt;/em&gt;, &lt;br/&gt; ახალი ხაზი
            </small>
          </div>

          <div className="post-form-actions-row">
            {editingPost && (
              <button 
                type="button"
                onClick={handleCancelEditPost}
                className="post-form-cancel-btn"
              >
                ❌ გაუქმება
              </button>
            )}
            <button 
              type="button"
              onClick={(e) => editingPost ? handleUpdatePost(e, false) : handleAddPost(e, false)}
              className="post-form-save-btn"
            >
              💾 შენახვა
            </button>
            <button 
              type="button"
              onClick={(e) => editingPost ? handleUpdatePost(e, true) : handleAddPost(e, true)}
              className="post-form-publish-btn"
            >
              📤 გამოქვეყნება
            </button>
          </div>
        </form>
      </div>

      {/* Posts List */}
      <div className="posts-list" style={{marginTop: '2rem'}}>
        <h3>გამოქვეყნებული კომენტარები ({posts.filter(p => p.published).length})</h3>
        <h3>გამოუქვეყნებელი ({posts.filter(p => !p.published).length})</h3>
        
        <div style={{display: 'grid', gap: '1rem', marginTop: '1rem'}}>
          {posts.map(post => (
            <div 
              key={post.id} 
              className="post-item"
              style={{
                background: post.published ? 'rgba(76, 175, 80, 0.1)' : 'rgba(255,255,255,0.05)',
                padding: '1.5rem',
                borderRadius: '8px',
                border: `1px solid ${post.published ? 'rgba(76, 175, 80, 0.3)' : 'rgba(255,255,255,0.1)'}`
              }}
            >
              <div className="post-item-header">
                <div className="post-item-header-info">
                  <h4 style={{margin: 0, fontSize: '1.2rem', color: '#fff'}}>{post.title}</h4>
                  <div style={{fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)', marginTop: '0.5rem'}}>
                    {post.category && <span>📂 {post.category} • </span>}
                    {post.author && <span>✍️ {post.author} • </span>}
                    <span>📅 {new Date(post.createdAt).toLocaleDateString('ka-GE')}</span>
                    {post.published ? 
                      <span style={{marginLeft: '0.5rem', color: '#4CAF50'}}>✅ გამოქვეყნებული</span> : 
                      <span style={{marginLeft: '0.5rem', color: '#FFC107'}}>⏳ არ არის გამოქვეყნებული</span>
                    }
                  </div>
                </div>
                <div className="post-item-actions">
                  <button
                    onClick={() => handleTogglePublish(post)}
                    className="edit-btn-small"
                    title={post.published ? 'მოხსნა პუბლიკაციიდან' : 'გამოქვეყნება'}
                  >
                    {post.published ? '👁️' : '📤'}
                  </button>
                  <button
                    onClick={() => handleEditPost(post)}
                    className="edit-btn-small"
                    title="რედაქტირება"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => handleDeletePost(post.id)}
                    className="delete-btn-small"
                    title="წაშლა"
                  >
                    🗑️
                  </button>
                </div>
              </div>
              
              {post.image && (
                <div style={{marginBottom: '1rem'}}>
                  <img 
                    src={post.image} 
                    alt={post.title}
                    style={{maxWidth: '200px', height: 'auto', borderRadius: '4px'}}
                    onError={(e) => e.target.style.display = 'none'}
                  />
                </div>
              )}
              
              <div 
                style={{
                  color: 'rgba(255,255,255,0.7)',
                  fontSize: '0.95rem',
                  lineHeight: '1.6',
                  maxHeight: '100px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
                dangerouslySetInnerHTML={{ __html: post.content.substring(0, 300) + '...' }}
              />
            </div>
          ))}
          
          {posts.length === 0 && (
            <div style={{textAlign: 'center', padding: '2rem', color: 'rgba(255,255,255,0.5)'}}>
              📝 ჯერ არ არის დამატებული სიახლეები
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderDashboard = () => (
    <div className="dashboard-content">
      <div className="stats-grid">
        <motion.div className="stat-card" whileHover={{ scale: 1.02 }}>
          <div className="stat-icon">📊</div>
          <div className="stat-info">
            <h3>{stats.totalMatches}</h3>
            <p>სულ მატჩები</p>
          </div>
        </motion.div>
        
        <motion.div className="stat-card" whileHover={{ scale: 1.02 }}>
          <div className="stat-icon">🔴</div>
          <div className="stat-info">
            <h3>{stats.activeMatches}</h3>
            <p>აქტიური მატჩები</p>
          </div>
        </motion.div>
        
        <motion.div className="stat-card" whileHover={{ scale: 1.02 }}>
          <div className="stat-icon">📈</div>
          <div className="stat-info">
            <h3>{stats.totalPredictions}</h3>
            <p>სულ პროგნოზები</p>
          </div>
        </motion.div>
      </div>
      

    </div>
  );

  const renderMatches = () => (
    <div className="matches-content">
      <div className="matches-header">
        <h3>მატჩების მართვა</h3>
        <button 
          className="add-button"
          onClick={() => setActiveTab('add')}
        >
          + ახალი მატჩი
        </button>
      </div>
      
      <div className="matches-table">
        {matches.map(match => (
          <div key={match.id} className="match-row">
            <div className="match-info">
              <div className="match-title">
                {match.homeTeam} vs {match.awayTeam}
                {match.comment && (
                  <span className="comment-indicator" title="აქვს კომენტარი">
                    💬
                  </span>
                )}
                {match.confidence && (
                  <span className="confidence-badge">
                    {'⭐'.repeat(Number(match.confidence))}
                  </span>
                )}
              </div>
              <div className="match-meta">
                {match.league} • {match.date} • {match.matchTime}
                {match.riskLevel && (
                  <span className={`risk-badge risk-${match.riskLevel}`}>
                    {match.riskLevel === 'low' && '🟢'}
                    {match.riskLevel === 'medium' && '🟡'}
                    {match.riskLevel === 'high' && '🔴'}
                  </span>
                )}
              </div>
              <div className="match-prediction">
                {match.prediction} (კოეფ: {match.coefficient})
                {match.stake && match.stake !== '1' && (
                  <span className="stake-badge"> • ფსონი: {match.stake}x</span>
                )}
              </div>
            </div>
            
            <div className="match-actions">
              <select 
                value={match.status} 
                onChange={(e) => handleStatusChange(match.id, e.target.value)}
                className="status-select"
              >
                <option value="pending">მოლოდინში</option>
                <option value="live">ლაივი</option>
                <option value="win">მოგება</option>
                <option value="loss">წაგება</option>
              </select>
              
              <button 
                className="edit-button"
                onClick={() => handleEditMatch(match)}
                title="რედაქტირება"
              >
                ✏️
              </button>
              
              {confirmDeleteMatch === match.id ? (
                <div className="match-delete-confirm">
                  <button
                    className="confirm-yes-btn"
                    onClick={handleConfirmDeleteMatch}
                  >
                    ✓ წაშლა
                  </button>
                  <button
                    className="confirm-no-btn"
                    onClick={() => setConfirmDeleteMatch(null)}
                  >
                    ✕ გაუქმება
                  </button>
                </div>
              ) : (
                <button 
                  className="delete-button"
                  onClick={() => handleDeleteMatch(match.id)}
                  title="წაშლა"
                >
                  🗑️
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderForm = (isEdit = false) => {
    const formData = isEdit ? editingMatch : newMatch;
    const setFormData = isEdit ? setEditingMatch : setNewMatch;
    const handleSubmit = isEdit ? handleUpdateMatch : handleAddMatch;
    
    // Get predictions for selected category
    const getPredictionsForCategory = () => {
      if (!formData.predictionCategory) return [];
      return predictionCategories[formData.predictionCategory]?.predictions || [];
    };
    
    return (
      <div className="form-content">
        <h3>{isEdit ? 'მატჩის რედაქტირება' : 'ახალი მატჩის დამატება'}</h3>
        
        <form onSubmit={handleSubmit} className="match-form">
          {/* Basic Info Section */}
          <div className="form-section">
            <h4 className="section-title">📋 ძირითადი ინფორმაცია</h4>
            
            <div className="form-row">
              <div className="form-group">
                <label>ლიგა ან ტურნირი</label>
                <select 
                  value={formData.league} 
                  onChange={(e) => setFormData({...formData, league: e.target.value, homeTeam: '', awayTeam: ''})}
                  required
                >
                  <option value="">აირჩიეთ ლიგა ან ტურნირი</option>
                  <optgroup label="🏆 ლიგები">
                    {leagues.map(league => (
                      <option key={league} value={league}>{league}</option>
                    ))}
                  </optgroup>
                  <optgroup label="🏅 ევროპული ტურნირები">
                    {Object.entries(tournaments).map(([key, tournament]) => (
                      <option key={key} value={key}>{tournament.name}</option>
                    ))}
                  </optgroup>
                </select>
              </div>
              
              <div className="form-group">
                <label>კატეგორია</label>
                <select 
                  value={formData.category} 
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                >
                  <option value="regular">⚽ რეგულარული მატჩი</option>
                  <option value="high-risk">🔮 მაღალი რისკი</option>
                  <option value="live">🔴 ლაივ ტიკეტი</option>
                  <option value="forecast">📋 წინასწარი პროგნოზი</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>სტატუსი</label>
                <select 
                  value={formData.status} 
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                >
                  <option value="pending">⏳ მოლოდინში</option>
                  <option value="live">🔴 ლაივი</option>
                  <option value="win">✅ მოგება</option>
                  <option value="loss">❌ წაგება</option>
                </select>
              </div>
            </div>
          </div>
          
          {/* Teams Section */}
          <div className="form-section">
            <h4 className="section-title">🏟️ გუნდები</h4>
            
            <div className="form-row">
              <div className="form-group">
                <label>სახლის გუნდი</label>
                <select 
                  value={formData.homeTeam} 
                  onChange={(e) => setFormData({...formData, homeTeam: e.target.value})}
                  required
                >
                  <option value="">აირჩიეთ გუნდი</option>
                  {formData.league && clubs[formData.league] && (
                    clubs[formData.league].map(club => (
                      <option key={club} value={club}>{club}</option>
                    ))
                  )}
                  {formData.league && tournaments[formData.league] && (
                    tournaments[formData.league].teams.map((team, idx) => (
                      <option key={idx} value={team.name}>{team.name}</option>
                    ))
                  )}
                </select>
              </div>
              
              <div className="form-group">
                <label>სტუმარი გუნდი</label>
                <select 
                  value={formData.awayTeam} 
                  onChange={(e) => setFormData({...formData, awayTeam: e.target.value})}
                  required
                >
                  <option value="">აირჩიეთ გუნდი</option>
                  {formData.league && clubs[formData.league] && (
                    clubs[formData.league].map(club => (
                      <option key={club} value={club}>{club}</option>
                    ))
                  )}
                  {formData.league && tournaments[formData.league] && (
                    tournaments[formData.league].teams.map((team, idx) => (
                      <option key={idx} value={team.name}>{team.name}</option>
                    ))
                  )}
                </select>
              </div>
            </div>
          </div>
          
          {/* Date & Time Section */}
          <div className="form-section">
            <h4 className="section-title">📅 თარიღი და დრო</h4>
            
            <div className="form-row">
              <div className="form-group">
                <label>თარიღი</label>
                <input 
                  type="date" 
                  value={formData.date} 
                  onChange={(e) => setFormData({...formData, date: e.target.value})}
                  required 
                />
              </div>
              
              <div className="form-group">
                <label>დრო</label>
                <ClockTimePicker
                  value={formData.matchTime}
                  onChange={(time) => setFormData({...formData, matchTime: time})}
                  placeholder="HH:MM (მაგ: 22:00)"
                />
              </div>
            </div>
          </div>
          
          {/* Prediction Section - Enhanced */}
          <div className="form-section prediction-section-enhanced">
            <h4 className="section-title">🎯 პროგნოზი და კოეფიციენტი</h4>
            
            <div className="form-row">
              <div className="form-group">
                <label>პროგნოზის კატეგორია</label>
                <select 
                  value={formData.predictionCategory} 
                  onChange={(e) => setFormData({
                    ...formData, 
                    predictionCategory: e.target.value,
                    prediction: '' // Reset prediction when category changes
                  })}
                  required
                >
                  <option value="">აირჩიეთ კატეგორია</option>
                  {Object.entries(predictionCategories).map(([key, category]) => (
                    <option key={key} value={key}>{category.name}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {formData.predictionCategory && (
              <div className="form-row">
                <div className="form-group">
                  <label>პროგნოზი</label>
                  <select 
                    value={formData.prediction} 
                    onChange={(e) => setFormData({...formData, prediction: e.target.value})}
                    required
                  >
                    <option value="">აირჩიეთ პროგნოზი</option>
                    {getPredictionsForCategory().map((pred, idx) => {
                      const value = typeof pred === 'string' ? pred : pred.value;
                      const label = typeof pred === 'string' ? pred : pred.label;
                      return (
                        <option key={`${value}-${idx}`} value={value}>
                          {label}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>
            )}
            
            {formData.prediction === 'სხვა ზუსტი ანგარიში' && (
              <div className="form-row">
                <div className="form-group">
                  <label>საკუთარი ზუსტი ანგარიში</label>
                  <input 
                    type="text" 
                    value={formData.customPrediction} 
                    onChange={(e) => setFormData({...formData, customPrediction: e.target.value})}
                    placeholder="მაგ: 4:2"
                  />
                </div>
              </div>
            )}
            
            <div className="form-row">
              <div className="form-group">
                <label>კოეფიციენტი</label>
                <input 
                  type="number" 
                  step="0.01" 
                  min="1.01" 
                  max="50.00" 
                  value={formData.coefficient} 
                  onChange={(e) => setFormData({...formData, coefficient: e.target.value})}
                  placeholder="მაგ: 1.85"
                  required
                />
              </div>
              
              <div className="form-group">
                <label>პროგნოზირებული ანგარიში</label>
                <input 
                  type="text" 
                  value={formData.predictedScore} 
                  onChange={(e) => setFormData({...formData, predictedScore: e.target.value})}
                  placeholder="მაგ: 2:1"
                />
              </div>
            </div>
          </div>
          
          {/* Analysis & Comment Section */}
          <div className="form-section">
            <h4 className="section-title">📝 ანალიზი და კომენტარი</h4>
            
            <div className="form-row">
              <div className="form-group full-width">
                <label>დეტალური ანალიზი</label>
                <textarea 
                  value={formData.analysis}
                  onChange={(e) => setFormData({...formData, analysis: e.target.value})}
                  placeholder="მატჩის დეტალური ანალიზი, ფორმა, სტატისტიკა..."
                  rows="3"
                />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group full-width">
                <label>კომენტარი (გამოჩნდება ტიკეტზე 💬 აიქონით)</label>
                <textarea 
                  value={formData.comment}
                  onChange={(e) => setFormData({...formData, comment: e.target.value})}
                  placeholder="მნიშვნელოვანი ინფორმაცია, რჩევები, ან განახლებები..."
                  rows="2"
                />
              </div>
            </div>
          </div>
          
          {/* Links Section */}
          <div className="form-section">
            <h4 className="section-title">🔗 ლინკები</h4>
            
            <div className="form-row">
              <div className="form-group full-width">
                <label>Livescore ლინკი</label>
                <input 
                  type="url" 
                  value={formData.livescoreUrl}
                  onChange={(e) => setFormData({...formData, livescoreUrl: e.target.value})}
                  placeholder="https://www.livescore.com/..."
                />
              </div>
            </div>
          </div>
          
          {/* Score Section - Only for finished matches */}
          {(formData.status === 'win' || formData.status === 'loss') && (
            <div className="form-section">
              <h4 className="section-title">⚽ საბოლოო ანგარიში</h4>
              
              <div className="form-row">
                <div className="form-group">
                  <label>საბოლოო ანგარიში</label>
                  <div className="score-inputs">
                    <input 
                      type="number" 
                      min="0" 
                      max="20" 
                      value={formData.homeScore} 
                      onChange={(e) => setFormData({...formData, homeScore: e.target.value})}
                      placeholder="სახლის"
                      className="score-input"
                    />
                    <span className="score-separator">:</span>
                    <input 
                      type="number" 
                      min="0" 
                      max="20" 
                      value={formData.awayScore} 
                      onChange={(e) => setFormData({...formData, awayScore: e.target.value})}
                      placeholder="სტუმრის"
                      className="score-input"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div className="form-actions">
            <button type="submit" className="submit-button">
              {isEdit ? '✅ განახლება' : '➕ დამატება'}
            </button>
            <button 
              type="button" 
              className="cancel-button"
              onClick={() => {
                if (isEdit) {
                  setEditingMatch(null);
                  setActiveTab('matches');
                } else {
                  setActiveTab('matches');
                }
              }}
            >
              ❌ გაუქმება
            </button>
          </div>
        </form>
      </div>
    );
  };

  return (
    <motion.div 
      className="admin-dashboard"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="admin-header">
        <div className="admin-title">
          <h1>🏆 NTF ადმინისტრაცია</h1>
          <p>სპორტული ფსონების მართვის პანელი</p>
        </div>
        <button className="logout-button" onClick={handleLogout}>
          გასვლა 🚪
        </button>
      </div>
      
      <div className="admin-content">
        <nav className="admin-nav">
          <button 
            className={`nav-button ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('dashboard')}
          >
            📊 დაშბორდი
          </button>
          <button 
            className={`nav-button ${activeTab === 'matches' ? 'active' : ''}`}
            onClick={() => setActiveTab('matches')}
          >
            ⚽ მატჩები
          </button>
          <button 
            className={`nav-button ${activeTab === 'add' ? 'active' : ''}`}
            onClick={() => setActiveTab('add')}
          >
            ➕ ახალი მატჩი
          </button>
          <button 
            className={`nav-button ${activeTab === 'leagues' ? 'active' : ''}`}
            onClick={() => setActiveTab('leagues')}
          >
            🏆 ლიგები & კლუბები
          </button>
          <button 
            className={`nav-button ${activeTab === 'predictions' ? 'active' : ''}`}
            onClick={() => setActiveTab('predictions')}
          >
            🎯 პროგნოზები
          </button>
          <button 
            className={`nav-button ${activeTab === 'tournaments' ? 'active' : ''}`}
            onClick={() => setActiveTab('tournaments')}
          >
            🏅 ევროპული ტურნირები
          </button>
          <button 
            className={`nav-button ${activeTab === 'posts' ? 'active' : ''}`}
            onClick={() => setActiveTab('posts')}
          >
            📰 კომენტარები
          </button>
        </nav>
        
        <main className="admin-main">
          {activeTab === 'dashboard' && renderDashboard()}
          {activeTab === 'matches' && renderMatches()}
          {activeTab === 'add' && renderForm()}
          {activeTab === 'edit' && renderForm(true)}
          {activeTab === 'leagues' && renderLeagueManagement()}
          {activeTab === 'predictions' && renderPredictionManagement()}
          {activeTab === 'tournaments' && renderTournamentManagement()}
          {activeTab === 'posts' && renderPostsManagement()}
        </main>
      </div>
    </motion.div>
  );
};

export default AdminDashboard;